import type { Tone } from "@/components/ui/status-tone";
import type {
  Campaign,
  CampaignKind,
  CampaignStatus,
  DeliveryFailureKind,
  NoticeSeverity,
} from "@/types/api";

/**
 * Labels and tones shared by the campaign list, the detail drawer and the
 * inbox. Kept out of the component files so each of those exports only
 * components — mixing the two breaks Vite's fast refresh for the module.
 */

export const KIND_LABEL: Record<CampaignKind, string> = {
  player_push: "Player push",
  admin_notice: "Admin notice",
};

export const STATUS_LABEL: Record<CampaignStatus, string> = {
  scheduled: "Scheduled",
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function campaignStatusTone(status: CampaignStatus): Tone {
  switch (status) {
    case "sent":
      return "success";
    case "sending":
    case "scheduled":
      return "info";
    case "failed":
      return "danger";
    default:
      return "neutral";
  }
}

/** Still moving — the counts will change, so the detail view polls. */
export function isInFlight(status: CampaignStatus): boolean {
  return status === "queued" || status === "sending";
}

/** The only two statuses the API will cancel; anything else is a 409. */
export function isCancellable(status: CampaignStatus): boolean {
  return status === "scheduled" || status === "queued";
}

/**
 * Failure kinds, with the tone each deserves.
 *
 * `stale_token` is deliberately neutral: a device that has gone away is the
 * normal state of an old audience, and colouring it red trains people to
 * ignore red. `invalid_payload` is the one that means the campaign itself was
 * wrong.
 */
export const FAILURE_META: Record<
  DeliveryFailureKind,
  { label: string; hint: string; tone: Tone }
> = {
  stale_token: {
    label: "Stale token",
    hint: "The device is gone and its token has been pruned. Normal on an old audience.",
    tone: "neutral",
  },
  invalid_payload: {
    label: "Invalid payload",
    hint: "The message was rejected as malformed. Check the title, body and data before resending.",
    tone: "danger",
  },
  throttled: {
    label: "Throttled",
    hint: "The push provider rate-limited these sends.",
    tone: "warning",
  },
  unknown: {
    label: "Unknown",
    hint: "The provider gave no reason.",
    tone: "neutral",
  },
};

export const SEVERITY_LABEL: Record<NoticeSeverity, string> = {
  info: "Info",
  warning: "Warning",
  critical: "Critical",
};

export function severityTone(severity: NoticeSeverity): Tone {
  if (severity === "critical") return "danger";
  if (severity === "warning") return "warning";
  return "neutral";
}

/** `announcement` → `Announcement`, `match_result` → `Match result`. */
export function humanise(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * One line describing who a campaign went to.
 *
 * `target` is typed `unknown` on the wire — it is whatever the send carried —
 * so it is probed rather than cast.
 */
export function describeTarget(campaign: Pick<Campaign, "target_kind" | "target">): string {
  const target =
    typeof campaign.target === "object" && campaign.target !== null
      ? (campaign.target as Record<string, unknown>)
      : {};
  const count = (key: string) => (Array.isArray(target[key]) ? target[key].length : 0);

  switch (campaign.target_kind) {
    case "all":
      return "All players";
    case "all_sub_admins":
      return "All sub-admins";
    case "player_ids":
      return `${String(count("player_ids"))} player${count("player_ids") === 1 ? "" : "s"}`;
    case "admin_ids":
      return `${String(count("admin_ids"))} admin${count("admin_ids") === 1 ? "" : "s"}`;
    case "admin_roles":
      return `${String(count("role_ids"))} role${count("role_ids") === 1 ? "" : "s"}`;
    case "segment": {
      const segment =
        typeof target.segment === "object" && target.segment !== null
          ? (target.segment as Record<string, unknown>)
          : {};
      const parts: string[] = [];
      if (typeof segment.active_since === "string")
        parts.push(`active since ${segment.active_since}`);
      if (Array.isArray(segment.platforms) && segment.platforms.length > 0) {
        parts.push(segment.platforms.join("/"));
      }
      if (typeof segment.min_level === "number" || typeof segment.max_level === "number") {
        parts.push(
          `level ${segment.min_level !== undefined ? String(segment.min_level) : "…"}–${
            segment.max_level !== undefined ? String(segment.max_level) : "…"
          }`,
        );
      }
      if (typeof segment.country === "string") parts.push(segment.country);
      return parts.length > 0 ? `Segment: ${parts.join(", ")}` : "Segment";
    }
    default:
      return humanise(campaign.target_kind);
  }
}
